<template>
  <div style="flex: 1; min-width: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 8px">
    <template v-for="(crumb, index) in crumbs">
      <span v-if="index" :key="'sep-' + index" style="color: var(--text-ph); font-size: 12px">/</span>
      <!-- every crumb re-queries, the current one too: it is the manual refresh -->
      <button
        :key="'crumb-' + index"
        type="button"
        class="mv-crumb"
        ellipsis
        :class="{ 'is-current': index === crumbs.length - 1, 'is-drop': dropTarget === 'crumb-' + index }"
        @click="$store.state.getFilteredAssets(index)"
        @dragover.prevent="dropTarget = $store.state.canEditIn(crumb) ? 'crumb-' + index : ''"
        @dragleave="dropTarget = ''"
        @drop="onDropOnCrumb($event, crumb)">
        {{ index ? assetTitle(crumb) : "All assets" }}
      </button>
    </template>

    <v-menu v-if="current && folderActions.length" offset-y content-class="mv-menu">
      <template v-slot:activator="{ on }">
        <button type="button" class="mv-rowbtn" title="Folder actions" aria-label="Folder actions" v-on="on">
          <v-icon size="16">mdi-dots-horizontal</v-icon>
        </button>
      </template>
      <v-list dense>
        <v-list-item v-for="action in folderActions" :key="action.label" @click="action.run()">
          <v-list-item-title>{{ action.label }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <template v-if="showChips && folders.length">
      <span style="width: 1px; height: 16px; margin: 0 4px; background: var(--line)"></span>
      <button
        v-for="folder in folders"
        :key="folder.id"
        type="button"
        class="mv-folder-chip"
        :class="{ 'is-drop': dropTarget === folder.id }"
        title="Open folder"
        :draggable="$store.state.canEdit(folder)"
        @dragstart="onDragStart($event, folder)"
        @dragover.prevent="dropTarget = $store.state.canEdit(folder) ? folder.id : ''"
        @dragleave="dropTarget = ''"
        @drop="onDropOnChip($event, folder)"
        @click="$store.state.setParentAsset(folder)">
        <span class="mv-folder-icon"></span>
        <span ellipsis>{{ assetTitle(folder) }}</span>
        <span v-if="countOf(folder) != null" class="mono" style="font-size: 10px; color: var(--text-faint)">{{ countOf(folder) }}</span>
      </button>
    </template>

    <template v-if="$store.state.cutAssets.length && canEditHere">
      <button type="button" class="mv-ghost mv-ghost--sm" title="Move the cut asset into this folder" @click="$store.state.pasteCutAssets()">
        Paste here ({{ $store.state.cutAssets.length }})
      </button>
      <button type="button" class="mv-rowbtn" title="Cancel move" aria-label="Cancel move" @click="$store.state.cutAssets = []">✕</button>
    </template>
  </div>
</template>

<script>
import { assetTitle } from "@/scripts/assetLabels.js";

export default {
  props: {
    folders: { type: Array, default: () => [] },
    showChips: { type: Boolean, default: true },
  },
  data() {
    return { dropTarget: "" };
  },
  computed: {
    crumbs() {
      return this.$store.state.filterParentAssets;
    },
    /** The open folder; null at the root. */
    current() {
      return this.crumbs.length > 1 ? this.crumbs[this.crumbs.length - 1] : null;
    },
    canEditHere() {
      return this.$store.state.canEditIn(this.$store.state.getLastFilterParentAsset());
    },
    /** Chip folders whose count is not known yet. */
    missingIds() {
      return this.showChips ? this.folders.map((folder) => folder.id).filter((id) => !(id in this.$store.state.folderCounts)) : [];
    },
    missingKey() {
      return this.missingIds.join();
    },
    folderActions() {
      const state = this.$store.state;
      const folder = this.current;
      return [
        { label: "Rename…", allowed: state.canEdit(folder), run: () => state.renameFolder(folder) },
        { label: "Permissions…", allowed: state.canManage(folder), run: () => state.openPermissionsDialog(folder) },
        { label: "Cut to move", allowed: state.canEdit(folder), run: () => state.cutAsset(folder) },
        { label: "Delete…", allowed: state.canEdit(folder), run: () => state.deleteFolder(folder) },
      ].filter((action) => action.allowed);
    },
  },
  watch: {
    // debounced: a burst of invalidations costs one read per chip
    missingKey: {
      immediate: true,
      handler() {
        clearTimeout(this.countTimer);
        this.countTimer = setTimeout(() => this.$store.state.countFolderChildren(this.missingIds), 300);
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.countTimer);
  },
  methods: {
    assetTitle,
    countOf(folder) {
      const count = this.$store.state.folderCounts[folder.id];
      if (count == null) return null;
      return count >= 100 ? "99+" : count;
    },
    draggedAsset(event) {
      const id = event.dataTransfer.getData("assetID");
      return id ? this.$store.state.getClipById(id) : null;
    },
    onDragStart(event, folder) {
      event.dataTransfer.setData("assetID", folder.id);
      event.dataTransfer.effectAllowed = "move";
    },
    onDropOnCrumb(event, crumb) {
      this.dropTarget = "";
      const asset = this.draggedAsset(event);
      if (!asset || !this.$store.state.canEditIn(crumb)) return;
      this.$store.state.moveAssets([asset], crumb.id || null);
    },
    onDropOnChip(event, folder) {
      this.dropTarget = "";
      const asset = this.draggedAsset(event);
      if (!asset || asset.id === folder.id || !this.$store.state.canEdit(folder)) return;
      this.$store.state.moveAssets([asset], folder.id);
    },
  },
};
</script>
